/**
 * Settings Tab Component
 *
 * @package TopBuddy
 */

import { __ } from '@wordpress/i18n';
import { useState, useEffect } from '@wordpress/element';
import {
	RadioControl,
	TextControl,
	ToggleControl,
	SelectControl,
	RangeControl,
	Notice,
	Button,
	__experimentalDivider as Divider,
	__experimentalToggleGroupControl as ToggleGroupControl,
	__experimentalToggleGroupControlOption as ToggleGroupControlOption,
} from '@wordpress/components';
import {
	alignLeft,
	alignCenter,
	alignRight,
} from '@wordpress/icons';
import ProBadge from './ProBadge';
import apiFetch from '@wordpress/api-fetch';

const SettingsTab = ({ settings, updateSetting }) => {
	const [themeInfo, setThemeInfo] = useState({
		theme_slug: '',
		theme_name: '',
		selector: '',
		auto_detected: false,
	});
	const [isLoadingThemeInfo, setIsLoadingThemeInfo] = useState(true);

	const postSettingsData = window.topbuddyPostSettings || {};
	const isProActive = postSettingsData.isProActive || false;
	const upgradeUrl = postSettingsData.upgradeUrl || 'https://wptopbuddy.com/?utm_source=plugin&utm_medium=topbuddy-free-plugin&utm_campaign=topbuddy-free-plugin-admin';

	// Fetch theme info on mount
	useEffect(() => {
		apiFetch({ path: '/topbuddy/v1/theme-info' })
			.then((data) => {
				setThemeInfo(data);
				setIsLoadingThemeInfo(false);
			})
			.catch((error) => {
				console.error('Failed to fetch theme info:', error);
				setIsLoadingThemeInfo(false);
			});
	}, []);

	// Update theme selector via API
	const handleSelectorChange = (newSelector) => {
		setThemeInfo({ ...themeInfo, selector: newSelector });

		// Debounce API call
		clearTimeout(window.topbuddySelectorTimeout);
		window.topbuddySelectorTimeout = setTimeout(() => {
			apiFetch({
				path: '/topbuddy/v1/theme-selector',
				method: 'POST',
				data: { selector: newSelector },
			})
				.then((data) => {
					setThemeInfo(data);
				})
				.catch((error) => {
					console.error('Failed to update theme selector:', error);
				});
		}, 1000);
	};
	return (
		<div className="topbuddy-settings__tab-content">
			<div className="topbuddy-settings__field">
				<ToggleControl
					label={
						<>
							{__('Activate this notification bar', 'topbuddy')}
							{!settings.isActive && (
								<span style={{ color: '#d63638', marginLeft: '8px', fontWeight: 'normal', fontSize: '13px' }}>
									({__('This Notification Bar is not displayed', 'topbuddy')})
								</span>
							)}
						</>
					}
					checked={settings.isActive === true}
					onChange={(value) => updateSetting('isActive', value)}
					help={__('When enabled, this notification bar will be displayed on the frontend. Only one bar can be active at a time in the free version.', 'topbuddy')}
					__nextHasNoMarginBottom
				/>
			</div>
			<Divider style={{ marginTop: '5px', marginBottom: '20px' }} />
			<div className="topbuddy-settings__field">
				<RadioControl
					label={__('Notification Bar Position', 'topbuddy')}
					selected={settings.position}
					options={[
						{ label: __('Top', 'topbuddy'), value: 'top' },
						{ label: __('Bottom', 'topbuddy'), value: 'bottom' },
					]}
					onChange={(value) => updateSetting('position', value)}
					help={__('Choose where the notification bar will appear on the page.', 'topbuddy')}
				/>
			</div>
			<div className="topbuddy-settings__field">
			<ToggleControl
				label={__('Make the notification bar sticky', 'topbuddy')}
				checked={settings.isSticky === true}
				onChange={(value) => updateSetting('isSticky', value)}
				help={__('When enabled, the notification bar will remain fixed on the screen while scrolling.', 'topbuddy')}
				__nextHasNoMarginBottom
			/>
			</div>

			{!isLoadingThemeInfo && themeInfo.auto_detected && (
				<div className="topbuddy-settings__field">
					<Notice status="success" isDismissible={false}>
						{__('We automatically detected your theme (', 'topbuddy')}
						<strong>{themeInfo.theme_name}</strong>
						{__(') and applied compatibility settings.', 'topbuddy')}
					</Notice>
				</div>
			)}

			<div className="topbuddy-settings__field">
				<TextControl
					label={__('Theme Header Selector (CSS)', 'topbuddy')}
					value={themeInfo.selector}
					onChange={handleSelectorChange}
					placeholder={__('e.g., .site-header or #masthead', 'topbuddy')}
					help={__('CSS selector for your theme\'s header element. Used to push down the header when the sticky notification bar is at the top. Leave empty if not needed.', 'topbuddy')}
					__next40pxDefaultSize
					__nextHasNoMarginBottom
					disabled={isLoadingThemeInfo}
				/>
			</div>

			<div className="topbuddy-settings__field">
				<TextControl
					label={__('Content Max Width (px)', 'topbuddy')}
					type="number"
					value={parseInt(settings.barWidth) || 1200}
					onChange={(value) => updateSetting('barWidth', value + 'px')}
					help={__('Maximum width of the notification bar content container', 'topbuddy')}
					__next40pxDefaultSize
					__nextHasNoMarginBottom
				/>
			</div>
			<div className="topbuddy-settings__field">
				<ToggleGroupControl
					label={__('Alignment', 'topbuddy')}
					value={settings.alignment || 'center'}
					onChange={(value) => updateSetting('alignment', value)}
					isBlock
					__nextHasNoMarginBottom
					__next40pxDefaultSize
					style={{ maxWidth: '400px' }}
				>
					<ToggleGroupControlOption value="flex-start" icon={alignLeft} label={__('Left', 'topbuddy')} />
					<ToggleGroupControlOption value="center" icon={alignCenter} label={__('Center', 'topbuddy')} />
					<ToggleGroupControlOption value="flex-end" icon={alignRight} label={__('Right', 'topbuddy')} />
				</ToggleGroupControl>
			</div>
			<Divider style={{ marginTop: '5px', marginBottom: '20px' }} />
			<div className="topbuddy-settings__field">
				<ToggleControl
					label={__('Enable close button', 'topbuddy')}
					checked={settings.enableCloseButton !== false}
					onChange={(value) => updateSetting('enableCloseButton', value)}
					__nextHasNoMarginBottom
				/>
			</div>
			<div className="topbuddy-settings__field">
				<TextControl
					label={__('Show again after (x) days', 'topbuddy')}
					type="number"
					value={settings.showAgainAfterDays ?? 7}
					onChange={(value) => updateSetting('showAgainAfterDays', parseInt(value) || 0)}
					help={__('Number of days before showing the notification bar again after a user closes it.', 'topbuddy')}
					__next40pxDefaultSize
					__nextHasNoMarginBottom
				/>
			</div>

			<Divider style={{ marginTop: '20px', marginBottom: '20px' }} />

			<div className="topbuddy-settings__field">
				<ToggleControl
					label={
						<span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
							{__('Enable bar delay', 'topbuddy')}
							{!isProActive && (
								<>
									<ProBadge />
									<Button
										variant="link"
										href={upgradeUrl}
										target="_blank"
										rel="noopener noreferrer"
										style={{ textDecoration: 'none', textTransform: 'none', height: 'auto', padding: 0 }}
									>
										{__('Upgrade', 'topbuddy')}
										<span className="dashicons dashicons-external" style={{ fontSize: '14px', width: '14px', height: '14px', marginLeft: '3px' }}></span>
									</Button>
								</>
							)}
						</span>
					}
					checked={settings.enableBarDelay === true}
					onChange={(value) => updateSetting('enableBarDelay', value)}
					help={__('When enabled, the notification bar will appear after a specific delay.', 'topbuddy')}
					__nextHasNoMarginBottom
					disabled={!isProActive}
				/>
			</div>

			{settings.enableBarDelay && isProActive && (
				<div className="topbuddy-settings__field">
					<TextControl
						label={__('Delay (seconds)', 'topbuddy')}
						type="number"
						value={settings.barDelaySeconds ?? 5}
						onChange={(value) => updateSetting('barDelaySeconds', parseInt(value) || 0)}
						help={__('Number of seconds to wait before showing the notification bar.', 'topbuddy')}
						__next40pxDefaultSize
						__nextHasNoMarginBottom
					/>
				</div>
			)}
			<Divider style={{ marginTop: '20px', marginBottom: '20px' }} />
			<div className="topbuddy-settings__field">
				<SelectControl
					label={__('Device Visibility', 'topbuddy')}
					value={settings.deviceVisibility || 'everywhere'}
					onChange={(value) => updateSetting('deviceVisibility', value)}
					options={[
						{ label: __('Everywhere', 'topbuddy'), value: 'everywhere' },
						{ label: __('Desktop Only', 'topbuddy'), value: 'desktop' },
						{ label: __('Mobile & Tablet Only', 'topbuddy'), value: 'mobile' },
					]}
					help={__('Choose which devices the notification bar should appear on.', 'topbuddy')}
					__next40pxDefaultSize
					__nextHasNoMarginBottom
				/>
			</div>
			{settings.deviceVisibility !== 'everywhere' && (
			<div className="topbuddy-settings__field" style={{ maxWidth: '400px' }}>
				<RangeControl
					label={__('Breakpoint (px)', 'topbuddy')}
					value={settings.breakpoint ?? 768}
					onChange={(value) => updateSetting('breakpoint', parseInt(value))}
					min={320}
					max={1920}
					help={settings.deviceVisibility === 'desktop'
						? __('Hide bar on screens smaller than this breakpoint.', 'topbuddy')
						: __('Hide bar on screens equal to or larger than this breakpoint.', 'topbuddy')
					}
					__next40pxDefaultSize
				/>
			</div>
			)}
		</div>
	);
};

export default SettingsTab;
